import React, { useEffect } from 'react';

import { BackButton } from '../../../../../../components/Clickables/Controls/BackButton/BackButton';
import { Heading } from '../../../../../../components/Heading/Heading';
import { LoadingSpinner } from '../../../../../../components/LoadingSpinner/LoadingSpinner';
import { RegulationCards } from '../../../../../../components/RegulationCards/RegulationCards';
import { trackEvent } from '../../../../../../helpers/analytics/trackEvent';
import { useRegulationBoundaryAtLocation } from '../../../../../../helpers/hooks/fishrules/useRegulationBoundaryAtLocation';
import { useSpeciesAtLocation } from '../../../../../../helpers/hooks/fishrules/useSpeciesAtLocation';
import { useSpeciesById } from '../../../../../../helpers/hooks/fishrules/useSpeciesById';
import { useMapLocation } from '../../../../../../helpers/hooks/useMapLocation';
import { getAdditionalInformation } from '../../../../../../helpers/regulations/getAdditionalInformation';
import { isProhibitedLocation } from '../../../../../../helpers/regulations/isProhibitedLocation';
import { useExplore } from '../../../../context/ExploreContext';
import { getWaterType } from '../../helpers';
import { MapDrawerLayout } from '../../MapDrawerLayout';
import { MapDrawerNotification } from '../../MapDrawerNotification/MapDrawerNotification';

import { SingleSpeciesHeader } from './components/SingleSpeciesHeader';

interface IProps {
  fishrulesSpeciesId: number;
}

export const RegulationView = ({ fishrulesSpeciesId }: IProps) => {
  const mapLocation = useMapLocation();
  const { saveParamsInUrl } = useExplore();
  const coordinates = mapLocation?.coordinates;

  const { isLoading: isLoadingSpecies, species } = useSpeciesAtLocation(
    coordinates ? { fishrulesSpeciesId, ...coordinates } : undefined,
  );

  const { isLoading: isLoadingSingleSpecies, species: singleSpecies } = useSpeciesById({
    fishrulesSpeciesId,
  });

  const { isLoading: isLoadingBoundaries, boundaries } =
    useRegulationBoundaryAtLocation(coordinates);

  const boundaryName = boundaries && boundaries.length > 0 ? boundaries[0]?.name : '';

  useEffect(() => {
    if (species?.id && boundaryName !== '') {
      void trackEvent({
        name: 'mykiss_explore_regulation_viewed',
        properties: {
          regulation_id: species.id.toString(),
          specie_id: species.fish_id.toString(),
          species_name: species.species,
          boundaries: boundaryName,
          water_type: getWaterType({
            saltwater: species.saltwater,
            freshwater: species.freshwater,
          }),
        },
      });
    }
  }, [
    species?.id,
    species?.fish_id,
    species?.species,
    species?.saltwater,
    species?.freshwater,
    boundaryName,
  ]);

  const isLoading = isLoadingSpecies || isLoadingBoundaries;

  if (isLoading || isLoadingSingleSpecies) {
    return (
      <MapDrawerLayout
        headerContent={
          <BackButton
            onClick={() => {
              saveParamsInUrl({ drawerView: 'regulations', drawerViewId: undefined });
            }}
            variant="controlLakeFogBg"
          />
        }
        withCloseButton={false}
      >
        <LoadingSpinner loadingText="Loading regulations in the area..." className="mt-6" />
      </MapDrawerLayout>
    );
  }

  if (!species || !boundaries || boundaries.length === 0) {
    return (
      <MapDrawerLayout withCloseButton={false}>
        <SingleSpeciesHeader
          species={singleSpecies}
          coordinates={coordinates}
          boundaries={boundaries}
        />
        <MapDrawerNotification
          className="mt-[100px]"
          title={`No regulations for ${
            singleSpecies?.species || 'the selected species'
          } in this area`}
          description="Try another area or species to get more results"
        />
      </MapDrawerLayout>
    );
  }

  const { isProhibited, message } = isProhibitedLocation(boundaries);

  const additionalInformation = getAdditionalInformation({ species, boundaries });

  return (
    <MapDrawerLayout withCloseButton={false}>
      <SingleSpeciesHeader species={species} coordinates={coordinates} boundaries={boundaries} />
      <div className="mb-10 grid grid-cols-2 gap-2">
        <RegulationCards
          coordinates={coordinates!}
          species={species}
          infoCardSize="sm"
          isProhibitedLocation={isProhibited}
          prohibitedMessage={message}
        />
      </div>
      {additionalInformation.map(
        ({ order, visible, title, body }) =>
          visible && (
            <div className="mb-10" key={order}>
              <Heading variant="2xs" className="mb-3">
                {title}
              </Heading>
              {body}
            </div>
          ),
      )}
    </MapDrawerLayout>
  );
};
